import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "src/common/components/SearchInput";
import { ChevronDownIcon, SortAscendingIcon } from "@heroicons/react/outline";

export interface SortOption {
  key: string;
  label: string;
}

export interface SearchAndSortProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  sortOptions?: SortOption[];
  selectedSort?: string;
  onSortChange?: (key: string) => void;
  className?: string;
  searchWidth?: string;
}

const SearchAndSort = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  sortOptions = [],
  selectedSort,
  onSortChange,
  className = "",
  searchWidth = "min-w-[220px] max-w-md",
}: SearchAndSortProps) => {
  const { t } = useTranslation();
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const defaultPlaceholder = t("statsNew.search.placeholder");
  const placeholder = searchPlaceholder || defaultPlaceholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedSortLabel =
    sortOptions.find((opt) => opt.key === selectedSort)?.label || t("statsNew.sort.default");

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <SearchInput
        value={searchValue}
        onChange={onSearchChange}
        placeholder={placeholder}
        className={`flex-1 ${searchWidth}`}
      />

      {sortOptions.length > 0 && onSortChange && (
        <div className="relative" ref={sortRef}>
          <button
            onClick={() => setIsSortOpen(!isSortOpen)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <SortAscendingIcon className="h-5 w-5 text-gray-500" />
            <span className="text-gray-700">{selectedSortLabel}</span>
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-500 transition-transform ${
                isSortOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isSortOpen && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => {
                    onSortChange(option.key);
                    setIsSortOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                    selectedSort === option.key
                      ? "bg-green-50 text-green-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAndSort;
